import { gql } from '@apollo/client'
import { breakpointSm } from '@farol-ds/tokens'
import { BoxLockVariationType } from '@jusbrasil-web/shared-apollo-client/types'
import { useViewportSize } from '@jusbrasil-web/shared-hooks'
import { useEffect } from 'react'

import { useRegisterLockEvent } from '../../../hooks'
import { LockEventName } from '../../../types'
import { OneOfferLockContent } from '../../one-offer-lock-content'
import { LawsuitTwoOffersLockContent } from '../lawsuit-two-offers-lock-content'
import {
  LawsuitTwoOffersLockContainer_OfferLockDataFragment,
  LawsuitTwoOffersLockContainer_ViewerFragment,
} from './__generated__/lawsuit-two-offers-lock-container.graphql'

export interface LawsuitTwoOffersLockContainerProps {
  offerLockData: LawsuitTwoOffersLockContainer_OfferLockDataFragment
  viewer: LawsuitTwoOffersLockContainer_ViewerFragment
}

export function LawsuitTwoOffersLockContainer({
  offerLockData,
  viewer,
}: LawsuitTwoOffersLockContainerProps) {
  const { cards = [] } = offerLockData?.body || {}
  const isSingleOffer = cards.length === 1
  const { width } = useViewportSize({
    initialDelay: 0,
    delay: 50,
    initialWidth: window.innerWidth,
    initialHeight: window.innerHeight,
  })
  const isSmallDevice = width <= breakpointSm
  const showOneOffer = isSingleOffer || isSmallDevice
  const { registerLockEvent } = useRegisterLockEvent()

  const modalType = showOneOffer
    ? BoxLockVariationType.LawsuitOneOfferLock
    : BoxLockVariationType.LawsuitTwoOffersLock

  useEffect(() => {
    if (!offerLockData?.body?.cards) return

    const eventProperties = showOneOffer
      ? [
          {
            ...offerLockData.body.cards[0].lockEventProperties,
            modal_type: modalType,
          },
        ]
      : offerLockData.body.cards.map((card) => ({
          ...card.lockEventProperties,
          modal_type: modalType,
        }))

    registerLockEvent({
      eventName: 'lock_view' as LockEventName,
      eventProperties,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return isSingleOffer || isSmallDevice ? (
    <OneOfferLockContent
      viewer={viewer}
      offerLockData={{
        ...offerLockData,
        header: {
          ...offerLockData.header,
          ...(isSmallDevice
            ? {
                title: 'Entenda a situação atual do seu processo',
                subtitle:
                  'Assine para <b>entender de forma simplificada</b> todas as informações deste processo',
              }
            : {}),
        },
      }}
    />
  ) : (
    <LawsuitTwoOffersLockContent viewer={viewer} offerLockData={offerLockData} />
  )
}

LawsuitTwoOffersLockContainer.fragments = {
  offerLockData: gql`
    fragment LawsuitTwoOffersLockContainer_offerLockData on OfferLockData {
      ...OneOfferLockContent_offerLockData
      ...LawsuitTwoOffersLockContent_offerLockData
    }
    ${OneOfferLockContent.fragments.offerLockData}
    ${LawsuitTwoOffersLockContent.fragments.offerLockData}
  `,
  viewer: gql`
    fragment LawsuitTwoOffersLockContainer_viewer on Profile {
      ...OneOfferLockContent_viewer
      ...LawsuitTwoOffersLockContent_viewer
    }
    ${OneOfferLockContent.fragments.viewer}
    ${LawsuitTwoOffersLockContent.fragments.viewer}
  `,
}
